import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import {
  Bug,
  Building2,
  ChevronsUpDown,
  Clapperboard,
  CreditCard,
  FolderOpen,
  HardDrive,
  LogOut,
  Mail,
  Plus,
  Settings,
  Shield,
  User,
  Users,
  Film,
  Zap,
  Menu as MenuIcon,
} from 'lucide-react';
import { roleAtLeast } from '@vidmakr/shared';
import { useSession } from '@/lib/session';
import { useLogos, useMediaQuery, useOrgs, rememberOrg } from '@/lib/hooks';
import { api } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import { Avatar, Badge, Button, Menu, Progress, cx } from './ui';
import { openContextMenu } from './ContextMenu';
import { Splitter, usePanelSize } from './Splitter';
import { UploadsPanel } from './UploadsPanel';
import { DesktopMenuDownload, DesktopSidebarPromo } from './DesktopApp';
import { desktop } from '@/lib/desktop';
import { NotificationBell } from './Notifications';
import { useOrgPlan } from '@/lib/billing';
import { AssetsTree, ProjectsTree } from './SidebarTree';
import { useBugReportsEnabled } from './BugReport';
import { openBugReport } from '@/lib/bugReport';

function NavItem({ to, icon, children, end }: { to: string; icon: React.ReactNode; children: React.ReactNode; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cx(
          'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm',
          isActive ? 'bg-panel-3 text-fg' : 'text-muted hover:bg-panel-2 hover:text-fg',
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}

export function ImpersonationBanner() {
  const impersonator = useSession((s) => s.impersonator);
  const user = useSession((s) => s.user);
  if (!impersonator) return null;
  return (
    <div className="flex items-center justify-center gap-3 bg-warn px-3 py-1.5 text-sm font-medium text-black">
      Signed in as {user?.name || user?.email} (by {impersonator.name || impersonator.email})
      <button
        className="rounded-md bg-black/80 px-2 py-0.5 text-xs text-white"
        onClick={async () => {
          await api('/auth/impersonate/stop', { method: 'POST' });
          window.location.href = '/admin/users';
        }}
      >
        Return to admin
      </button>
    </div>
  );
}

/** Sidebar width on wide screens (phones and tablets slide it in at a fixed width). */
const NAV_SIZE = { key: 'vm:sidebarW', initial: 240, min: 200, max: 420 };

export function AppShell() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);
  const { orgs, roles } = useOrgs();
  const logos = useLogos();
  const [invites] = useTable(tables.orgInvite);
  const pendingInvites = useMemo(
    () => invites.filter((i) => i.status === 'pending' && i.email === user?.email?.toLowerCase() && !roles.has(i.orgId)),
    [invites, user, roles],
  );
  const org = orgs.find((o) => o.id === orgId);
  const plan = useOrgPlan(orgId);
  const bugReports = useBugReportsEnabled();
  const [navOpen, setNavOpen] = useState(false);
  const wide = useMediaQuery('(min-width: 1024px)');
  const [navW, setNavW] = usePanelSize(NAV_SIZE);
  const { pathname, search } = useLocation();
  useEffect(() => setNavOpen(false), [pathname, search]);
  // Browsing pages (projects, library, exports) use the whole width; forms and settings stay readable.
  const fullWidth = /^\/orgs\/[^/]+(\/(s\/.*|assets|exports))?\/?$/.test(pathname);
  useEffect(() => {
    if (orgId) rememberOrg(orgId);
  }, [orgId]);

  return (
    <div className="flex h-full flex-col">
      <ImpersonationBanner />
      <div className="relative flex min-h-0 flex-1">
        {/* Phones and tablets: the sidebar slides in over the page. */}
        {navOpen ? <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setNavOpen(false)} /> : null}
        <aside
          className={cx(
            'fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] shrink-0 flex-col overflow-y-auto border-r border-line bg-panel pb-[env(safe-area-inset-bottom)] transition-transform duration-200',
            'lg:static lg:z-auto lg:max-w-none lg:translate-x-0 lg:transition-none',
            navOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
          )}
          style={wide ? { width: navW } : undefined}
          onContextMenu={(e) => {
            // Navigation links keep the browser menu (open in new tab, copy link).
            if ((e.target as HTMLElement).closest('a')) return;
            openContextMenu(e, [
              {
                label: 'Switch organization',
                icon: <Building2 size={14} />,
                disabled: !orgs.length,
                submenu: orgs.map((o) => ({ label: o.name, checked: o.id === orgId, onClick: () => navigate(`/orgs/${o.id}`) })),
              },
              { label: 'New organization', icon: <Plus size={14} />, onClick: () => navigate('/new-org') },
              'sep',
              { label: 'Account', icon: <User size={14} />, onClick: () => navigate('/account') },
              ...(bugReports ? [{ label: 'Report a bug', icon: <Bug size={14} />, onClick: openBugReport }] : []),
              { label: 'Sign out', icon: <LogOut size={14} />, onClick: () => void logout() },
            ]);
          }}
        >
          <div className="flex h-14 items-center gap-2 px-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent">
              <Film size={15} className="text-white" />
            </div>
            <span className="flex-1 text-[15px] font-semibold tracking-tight">vidmakr</span>
            <span className="hidden lg:block">
              <NotificationBell align="left" />
            </span>
          </div>
          <div className="px-3">
            <Menu
              align="left"
              trigger={
                <button className="flex w-full items-center gap-2 rounded-lg border border-line bg-panel-2 px-2.5 py-2 text-left hover:bg-panel-3">
                  {org && logos.get(org.id) ? (
                    <img src={logos.get(org.id)} alt="" className="h-5 w-5 shrink-0 rounded object-cover" />
                  ) : (
                    <Building2 size={15} className="shrink-0 text-muted" />
                  )}
                  <span className="flex-1 truncate text-sm font-medium">{org?.name ?? 'Select organization'}</span>
                  <ChevronsUpDown size={14} className="text-faint" />
                </button>
              }
              items={[
                ...orgs.map((o) => ({
                  label: (
                    <span className="flex w-full items-center justify-between gap-3">
                      <span className="flex min-w-0 items-center gap-2">
                        {logos.get(o.id) ? <img src={logos.get(o.id)} alt="" className="h-4 w-4 shrink-0 rounded object-cover" /> : null}
                        <span className="truncate">{o.name}</span>
                      </span>
                      <span className="text-[11px] text-faint">{roles.get(o.id)}</span>
                    </span>
                  ),
                  onClick: () => navigate(`/orgs/${o.id}`),
                })),
                'sep' as const,
                { label: 'New organization', icon: <Plus size={14} />, onClick: () => navigate('/new-org') },
              ]}
            />
          </div>
          <nav className="mt-4 flex flex-col gap-0.5 px-3">
            {orgId ? (
              <>
                <NavItem to={`/orgs/${orgId}`} end icon={<Clapperboard size={16} />}>
                  Projects
                </NavItem>
                <ProjectsTree orgId={orgId} showGeneral={roles.get(orgId) !== 'guest'} />
                <div className="relative">
                  <NavItem to={`/orgs/${orgId}/assets`} icon={<FolderOpen size={16} />}>
                    Asset library
                  </NavItem>
                  <AssetsTree orgId={orgId} />
                </div>
                <NavItem to={`/orgs/${orgId}/exports`} icon={<HardDrive size={16} />}>
                  Exports
                </NavItem>
                {roles.get(orgId) !== 'guest' ? (
                  <NavItem to={`/orgs/${orgId}/members`} icon={<Users size={16} />}>
                    Members
                  </NavItem>
                ) : null}
                {roleAtLeast(roles.get(orgId) ?? null, 'admin') ? (
                  <NavItem to={`/orgs/${orgId}/workers`} icon={<Zap size={16} />}>
                    GPU workers
                  </NavItem>
                ) : null}
                {plan?.enabled && roles.get(orgId) !== 'guest' ? (
                  <NavItem to={`/orgs/${orgId}/billing`} icon={<CreditCard size={16} />}>
                    <span className="flex flex-1 items-center justify-between">
                      Plan & billing <span className="text-[11px] text-faint">{plan.limits.plan.label}</span>
                    </span>
                  </NavItem>
                ) : null}
                {roles.get(orgId) !== 'guest' ? (
                  <NavItem to={`/orgs/${orgId}/settings`} icon={<Settings size={16} />}>
                    Settings
                  </NavItem>
                ) : null}
              </>
            ) : null}
            <div className="my-2 border-t border-line" />
            <NavItem to="/invites" icon={<Mail size={16} />}>
              <span className="flex flex-1 items-center justify-between">
                Invitations {pendingInvites.length ? <Badge tone="accent">{pendingInvites.length}</Badge> : null}
              </span>
            </NavItem>
            {user?.isSuperadmin ? (
              <NavItem to="/admin" icon={<Shield size={16} />}>
                Admin panel
              </NavItem>
            ) : null}
          </nav>
          <div className="mt-auto space-y-3 p-3">
            <DesktopSidebarPromo />
            {org ? (
              <div className="rounded-xl border border-line bg-panel-2 p-3">
                <div className="mb-1.5 flex justify-between text-xs text-muted">
                  <span>Storage</span>
                  <span>
                    {formatBytes(org.storageUsedBytes)} / {formatBytes(org.storageLimitBytes)}
                  </span>
                </div>
                <Progress value={org.storageUsedBytes / Math.max(1, org.storageLimitBytes)} />
              </div>
            ) : null}
            <Menu
              align="left"
              trigger={
                <button className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-panel-2">
                  <Avatar name={user?.name || user?.email || '?'} url={user?.avatarUrl} size={28} />
                  <div className="min-w-0 flex-1 text-left">
                    <div className="truncate text-sm font-medium">{user?.name || 'Unnamed'}</div>
                    <div className="truncate text-xs text-faint">{user?.email}</div>
                  </div>
                </button>
              }
              items={[
                { label: 'Account', icon: <User size={14} />, onClick: () => navigate('/account') },
                ...(bugReports ? [{ label: 'Report a bug', icon: <Bug size={14} />, onClick: openBugReport }] : []),
                ...(desktop ? [] : ['sep' as const, { content: <DesktopMenuDownload /> }, 'sep' as const]),
                { label: 'Sign out', icon: <LogOut size={14} />, onClick: () => void logout() },
              ]}
            />
          </div>
        </aside>
        {wide ? <Splitter size={navW} onSize={setNavW} limits={NAV_SIZE} edge="right" label="Resize the sidebar" className="-mx-0.5" /> : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-panel px-3 lg:hidden">
            <Button variant="ghost" size="icon" aria-label="Open menu" onClick={() => setNavOpen(true)}>
              <MenuIcon size={20} />
            </Button>
            {org && logos.get(org.id) ? (
              <img src={logos.get(org.id)} alt="" className="h-7 w-7 rounded-md object-cover" />
            ) : (
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-accent">
                <Film size={14} className="text-white" />
              </div>
            )}
            <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">{org?.name ?? 'vidmakr'}</span>
            <NotificationBell />
            <button aria-label="Account" onClick={() => navigate('/account')}>
              <Avatar name={user?.name || user?.email || '?'} url={user?.avatarUrl} size={30} />
            </button>
          </header>
          <main className="min-w-0 flex-1 overflow-auto">
            <div className={cx('mx-auto px-4 py-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:px-6 lg:px-8 lg:py-8', fullWidth ? 'max-w-none' : 'max-w-[1400px]')}>
              <BillingBanner orgId={orgId} />
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      <UploadsPanel />
    </div>
  );
}

export function NewOrgButton() {
  const navigate = useNavigate();
  return (
    <Button variant="primary" onClick={() => navigate('/new-org')}>
      <Plus size={16} /> New organization
    </Button>
  );
}

/** Trial countdown and over-limit warning above every organization page. */
function BillingBanner({ orgId }: { orgId: string | undefined }) {
  const plan = useOrgPlan(orgId);
  const { pathname } = useLocation();
  if (!plan?.enabled || pathname.endsWith('/billing')) return null;
  const { org, trialDaysLeft } = plan;
  let text = '';
  let tone: 'warn' | 'danger' = 'warn';
  if (org.overLimitSince || org.storageUsedBytes > plan.limits.storageBytes) {
    text = 'This workspace is over its storage limit: uploads and new exports are paused.';
    tone = 'danger';
  } else if (org.billingStatus === 'past_due') {
    text = 'The last payment failed. Update your card to keep your plan.';
    tone = 'danger';
  } else if (trialDaysLeft !== null && trialDaysLeft <= 7) {
    text = `Your Pro trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}.`;
  }
  if (!text) return null;
  return (
    <div
      data-billing-banner
      className={cx('mb-5 flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3 text-sm', tone === 'danger' ? 'border-danger/40 bg-danger/10' : 'border-warn/40 bg-warn/10')}
    >
      <span>{text}</span>
      <NavLink to={`/orgs/${org.id}/billing`} className="font-medium text-accent-2 hover:underline">
        See plans
      </NavLink>
    </div>
  );
}
